import React from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { MdClose } from "react-icons/md";
import "react-quill/dist/quill.snow.css";
import { useNavigate, useParams } from "react-router-dom";
import Select from "react-select";
import { useGetExercises } from "../../api/useProducts/useExercises";
import { useCreateWorkOutExercises } from "../../api/useProducts/useWorkOut";
import { CustomButton, InputField } from "../../components";
import moment from "moment";
import { TimePicker } from "antd";
import CustomBreadcrumb from "../../components/CustomBreadcrumb";
import { useQueryClient } from "@tanstack/react-query";
// Form data interface
interface WorkoutFormData {
  exercises: { value: string; label: string };
  order: number;
  duration: number;
  level: { value: string; label: string };
  reps: number;
  sets: number;
}
interface Props {
  setIsMoreWorkOuts: (value: boolean) => void;
  WorkoutRefetch: () => void;
}
const UpdateAddWorkoutExercises: React.FC<Props> = ({
  setIsMoreWorkOuts,
  WorkoutRefetch,
}) => {
  const { id } = useParams();
  const { mutate, isPending } = useCreateWorkOutExercises(Number(id));
  const { exercisesData, exercisesLoading } = useGetExercises({
    page: 1,
    limit: 100000,
  });
  const navigate = useNavigate();
  const query = useQueryClient();

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
    control,
  } = useForm<WorkoutFormData>();

  const handleSubmitExercise: SubmitHandler<WorkoutFormData> = (data) => {
    const body = [
      {
        sets: parseInt(data.sets as unknown as string),
        reps: parseInt(data.reps as unknown as string),
        order: parseInt(data.order as unknown as string),
        // @ts-ignore
        duration: `${moment(data.duration.$d).format("HH:mm:ss")}`,
        exerciseId: parseInt(data.exercises.value),
      },
    ];

    mutate(body, {
      onSuccess: async (data) => {
        toast.success("successfully created");
        await query.resetQueries({
          queryKey: ["getWorkoutData"],
          exact: true,
        });
        // navigate(`/workouts`);
        setIsMoreWorkOuts(false);
        WorkoutRefetch();
      },
      onError: (error: any) => {
        toast.error(error.response.data.message);
      },
    });
  };
  const breadcrumbItems = [
    { title: "Workouts", link: "workouts" },
    { title: "Add Exercise" },
  ];
  return (
    <>
      <div className="mt-3">
        <CustomBreadcrumb items={breadcrumbItems} />
      </div>
      <div className="flex justify-between items-center my-2">
        <h1 className="font-medium mt-5 mb-4 text-2xl">Workout Exercise</h1>

        <CustomButton
          handleClick={() => setIsMoreWorkOuts(false)}
          icon={<MdClose size={22} />}
          rounded="rounded-full"
          padding="p-0"
          addclass="mt-4 ms-auto w-8 h-8"
        />
      </div>

      <form onSubmit={handleSubmit(handleSubmitExercise)}>
        <div>
          <div className="grid lg:grid-cols-2 grid-cols-1 gap-3 ">
            <InputField
              label="Reps"
              placeholder="Enter reps"
              type="number"
              register={register(`reps`, {
                required: "Reps are required",
                validate: (value) => {
                  // @ts-ignore
                  if (value <= 0) {
                    return "reps must be a positive number";
                  }
                  // @ts-ignore
                  if (!Number.isInteger(parseFloat(value))) {
                    return "reps must be a whole number";
                  }
                  return true;
                },
              })}
              error={errors.reps?.message}
            />
            <InputField
              label="Sets"
              placeholder="Enter set"
              type="number"
              register={register(`sets`, {
                required: "Sets are required",
                validate: (value) => {
                  // @ts-ignore
                  if (value <= 0) {
                    return "Sets must be a positive number";
                  }
                  // @ts-ignore
                  if (!Number.isInteger(parseFloat(value))) {
                    return "Sets must be a whole number";
                  }
                  return true;
                },
              })}
              error={errors.sets?.message}
            />
            <div>
              <p className="block text-gray-700 mb-2">Duration</p>

              <Controller
                name="duration"
                control={control}
                rules={{
                  required: "duration are required",
                  min: { value: 1, message: "duration must be at least 1" },
                }}
                render={({ field }) => (
                  // @ts-ignore
                  <TimePicker
                    {...field}
                    className="!focus:shadow-none w-full h-11 !focus:border-gray-300 hover:shadow-none hover:border-gray-300 text-sm"
                  />
                )}
              />
              {errors.duration && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.duration.message}
                </p>
              )}
            </div>

            <InputField
              label="order"
              placeholder="Enter order"
              type="number"
              register={register(`order`, {
                required: "Order is required",
                min: {
                  value: 1,
                  message: "Only positive numbers are allowed",
                },
              })}
              error={errors.order?.message}
            />

            <div>
              <p className="block text-gray-700 mb-2">Exercises</p>
              <Controller
                name={`exercises`}
                control={control}
                rules={{ required: "Exercise is required" }}
                render={({ field }) => (
                  <Select
                    {...field}
                    isDisabled={exercisesLoading}
                    options={exercisesData?.data.map((exercise) => ({
                      value: exercise.id.toString(),
                      label: exercise.title,
                    }))}
                    placeholder="Select exercises"
                    styles={{
                      control: (base) => ({
                        ...base,
                        height: "44px", // Background color for the select control
                        boxShadow: "none",
                        caretColor: "#24B1B3",
                        border: "1px solid #E5E7EB",
                        borderRadius: "8px",
                        fontSize: "14px",
                        "&:focus": {
                          outline: "none !important",
                          boxShadow: "none !important",
                          borderColor: "gray !important",
                        },
                        "&:hover": { borderColor: "none !important" },
                      }),
                      placeholder: (base) => ({
                        ...base,
                        color: "rgb(148 163 184)", // Set placeholder color here
                      }),
                    }}
                  />
                )}
              />
              {errors.exercises && (
                <p className="text-red-500 text-sm mt-1">
                  {errors.exercises?.message}
                </p>
              )}
            </div>
          </div>
        </div>

        <CustomButton
          type="submit"
          isLoading={isPending}
          btntext="Save"
          addclass="mt-5 mx-auto w-1/2"
        />
      </form>
    </>
  );
};

export default UpdateAddWorkoutExercises;
